/**
 * Projektname im Header mit Projektwechsel: ein Klick öffnet die Liste aller Projekte, darunter
 * „Alle Projekte“ zur Startseite.
 *
 * Nur Navigation, keine Aktionen am Projekt – „Projekt teilen“ steht im Einstellungsmenü.
 * Die Liste lädt erst beim Öffnen; wer nie wechselt, kostet keinen Abruf. Lange
 * Namen kürzt der Knopf mit „…“ – im Header steht er in der Breite der Sidebar.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router";
import {
  Box,
  ButtonBase,
  CircularProgress,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  ViewList as ViewListIcon,
} from "@mui/icons-material";
import { apiService } from "../../../services/api";
import { Windpark } from "../../../types";
import { colors } from "../../../styles/theme";

interface ProjectSwitcherProps {
  projectId: string;
  projectName: string;
}

const ProjectSwitcher: React.FC<ProjectSwitcherProps> = ({
  projectId,
  projectName,
}) => {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [projects, setProjects] = useState<Windpark[] | null>(null);

  const open = (event: React.MouseEvent<HTMLElement>) => {
    setAnchor(event.currentTarget);
    apiService
      .getProjects()
      .then((list: Windpark[]) =>
        setProjects(
          [...list].sort((a, b) => a.name.localeCompare(b.name, "de")),
        ),
      )
      .catch(() => setProjects([]));
  };

  const goTo = (path: string) => {
    setAnchor(null);
    navigate(path);
  };

  return (
    <>
      <ButtonBase
        onClick={open}
        aria-label="Projekt wechseln"
        aria-haspopup="menu"
        title={projectName}
        sx={{
          gap: 0.5,
          px: 1,
          py: 0.5,
          ml: -1,
          minWidth: 0,
          borderRadius: 1,
          font: "inherit",
          color: colors.brand.logoText,
          "&:hover": { backgroundColor: colors.state.hover },
        }}
      >
        <Box
          component="span"
          sx={{
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {projectName}
        </Box>
        <ExpandMoreIcon sx={{ color: colors.text.secondary }} />
      </ButtonBase>

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 240, maxHeight: 480 } } }}
      >
        {projects === null ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          projects.map((project) => (
            <MenuItem
              key={project.id}
              selected={project.id === projectId}
              onClick={() =>
                project.id === projectId
                  ? setAnchor(null)
                  : goTo(`/project/${project.id}/overview`)
              }
            >
              <ListItemIcon>
                {project.id === projectId && <CheckIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText>{project.name}</ListItemText>
            </MenuItem>
          ))
        )}
        <Divider />
        <MenuItem onClick={() => goTo("/")}>
          <ListItemIcon>
            <ViewListIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Alle Projekte</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ProjectSwitcher;
